import { useState, useCallback } from 'react';

export const useDSLState = () => {
  // Main config state
  const [config, setConfig] = useState({
    sections: {},
    includeData: {
      name: true,
      score: true,
      change: true,
      benchmark: true,
      outcomeScores: true,
      llmActions: true,
      question_scores: true,
      score_distribution: false,
      score_distribution_bands: { low: [0, 4], neutral: [5, 6], high: [7, 10] },
      anchor_breakdown: false,
      include_report_summary: false,
      include_action_recommendations: false
    }
  });

  // Modal states
  const [showAddSection, setShowAddSection] = useState(false);
  const [showAddNestedSection, setShowAddNestedSection] = useState(false);
  const [showAddLLMActionModal, setShowAddLLMActionModal] = useState(false);
  const [showEditLLMActionModal, setShowEditLLMActionModal] = useState(false);
  const [showEditSectionConfigModal, setShowEditSectionConfigModal] = useState(false);
  const [showRemoveSectionModal, setShowRemoveSectionModal] = useState(false);
  const [showRemoveLLMActionModal, setShowRemoveLLMActionModal] = useState(false);

  // Form states
  const [newSectionType, setNewSectionType] = useState('');
  const [newSectionName, setNewSectionName] = useState('');
  const [newNestedSectionType, setNewNestedSectionType] = useState('');
  const [newNestedSectionName, setNewNestedSectionName] = useState('');
  const [parentSectionName, setParentSectionName] = useState('');
  
  // LLM Action states
  const [selectedSectionName, setSelectedSectionName] = useState('');
  const [selectedActionId, setSelectedActionId] = useState(null);
  const [actionName, setActionName] = useState('');
  const [actionPrompt, setActionPrompt] = useState('');
  const [actionFeedbackType, setActionFeedbackType] = useState('current');
  const [actionEnabled, setActionEnabled] = useState(true);
  const [actionFormatAsJson, setActionFormatAsJson] = useState(false);
  const [llmActionsToEdit, setLLMActionsToEdit] = useState({});

  // Section config states
  const [selectedSectionConfigField, setSelectedSectionConfigField] = useState('');
  const [selectedSectionConfigValue, setSelectedSectionConfigValue] = useState('');

  // Removal states
  const [sectionToRemove, setSectionToRemove] = useState('');
  const [llmActionsToRemove, setLLMActionsToRemove] = useState([]);

  // UI states
  const [openSections, setOpenSections] = useState({});
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState('info');
  const [isLoading, setIsLoading] = useState(false);

  // Core functions
  const addSection = useCallback((type, sectionName, parentSectionId = null) => {
    const newSection = {
      id: Date.now(),
      name: sectionName,
      type,
      config: {
        num: (type === 'lowestScore' || type === 'highestScore' || type === 'factorDistributionHighest' || type === 'factorDistributionLowest') ? 3 : undefined,
        includeOutcomeScores: type === 'anchor' ? false : undefined,
        outcomeId: type === 'outcomeInsight' ? '' : undefined,
        includeFactorCorrelations: type === 'outcomeForEach' ? false : undefined
      },
      llmActions: [],
      nestedSections: type === 'anchor' ? {} : undefined
    };
    
    if (parentSectionId) {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [parentSectionId]: {
            ...prev.sections[parentSectionId],
            nestedSections: {
              ...prev.sections[parentSectionId].nestedSections,
              [sectionName]: newSection
            }
          }
        }
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [sectionName]: newSection
        }
      }));
    }
  }, []);

  const removeSection = useCallback((sectionName, parentSectionName = null) => {
    if (parentSectionName) {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [parentSectionName]: {
            ...prev.sections[parentSectionName],
            nestedSections: Object.fromEntries(
              Object.entries(prev.sections[parentSectionName].nestedSections || {})
                .filter(([key]) => key !== sectionName)
            )
          }
        }
      }));
    } else {
      setConfig(prev => {
        const newSections = { ...prev.sections };
        delete newSections[sectionName];
        return {
          ...prev,
          sections: newSections
        };
      });
    }
  }, []);

  const updateSectionConfig = useCallback((sectionName, field, value, parentSectionName = null) => {
    if (parentSectionName) {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [parentSectionName]: {
            ...prev.sections[parentSectionName],
            nestedSections: {
              ...prev.sections[parentSectionName].nestedSections,
              [sectionName]: {
                ...prev.sections[parentSectionName].nestedSections[sectionName],
                config: {
                  ...prev.sections[parentSectionName].nestedSections[sectionName].config,
                  [field]: value
                }
              }
            }
          }
        }
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [sectionName]: {
            ...prev.sections[sectionName],
            config: {
              ...prev.sections[sectionName].config,
              [field]: value
            }
          }
        }
      }));
    }
  }, []);

  const addLLMAction = useCallback((sectionName, parentSectionName = null, actionData = null) => {
    const newAction = actionData || {
      id: Date.now(),
      name: '',
      prompt: '',
      feedbackType: 'current',
      enabled: true,
      formatAsJson: false
    };
    
    if (parentSectionName) {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [parentSectionName]: {
            ...prev.sections[parentSectionName],
            nestedSections: {
              ...prev.sections[parentSectionName].nestedSections,
              [sectionName]: {
                ...prev.sections[parentSectionName].nestedSections[sectionName],
                llmActions: [...(prev.sections[parentSectionName].nestedSections[sectionName].llmActions || []), newAction]
              }
            }
          }
        }
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [sectionName]: {
            ...prev.sections[sectionName],
            llmActions: [...(prev.sections[sectionName].llmActions || []), newAction]
          }
        }
      }));
    }
  }, []);

  const removeLLMAction = useCallback((sectionName, actionId, parentSectionName = null) => {
    if (parentSectionName) {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [parentSectionName]: {
            ...prev.sections[parentSectionName],
            nestedSections: {
              ...prev.sections[parentSectionName].nestedSections,
              [sectionName]: {
                ...prev.sections[parentSectionName].nestedSections[sectionName],
                llmActions: prev.sections[parentSectionName].nestedSections[sectionName].llmActions.filter(action => action.id !== actionId)
              }
            }
          }
        }
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [sectionName]: {
            ...prev.sections[sectionName],
            llmActions: prev.sections[sectionName].llmActions.filter(action => action.id !== actionId)
          }
        }
      }));
    }
  }, []);

  const updateLLMAction = useCallback((sectionName, actionId, field, value, parentSectionName = null) => {
    if (parentSectionName) {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [parentSectionName]: {
            ...prev.sections[parentSectionName],
            nestedSections: {
              ...prev.sections[parentSectionName].nestedSections,
              [sectionName]: {
                ...prev.sections[parentSectionName].nestedSections[sectionName],
                llmActions: prev.sections[parentSectionName].nestedSections[sectionName].llmActions.map(action => 
                  action.id === actionId 
                    ? { ...action, [field]: value }
                    : action
                )
              }
            }
          }
        }
      }));
    } else {
      setConfig(prev => ({
        ...prev,
        sections: {
          ...prev.sections,
          [sectionName]: {
            ...prev.sections[sectionName],
            llmActions: prev.sections[sectionName].llmActions.map(action => 
              action.id === actionId 
                ? { ...action, [field]: value }
                : action
            )
          }
        }
      }));
    }
  }, []);

  const toggleIncludeData = useCallback((field) => {
    setConfig(prev => ({
      ...prev,
      includeData: {
        ...prev.includeData,
        [field]: !prev.includeData[field]
      }
    }));
  }, []);

  const setIncludeDataField = useCallback((field, value) => {
    setConfig(prev => ({
      ...prev,
      includeData: {
        ...prev.includeData,
        [field]: value
      }
    }));
  }, []);

  const toggleSection = useCallback((sectionName) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  }, []);

  const isSectionOpen = useCallback((sectionName) => {
    return openSections[sectionName] || false;
  }, [openSections]);

  const showToastMessage = useCallback((message, type = 'info') => {
    setToastMessage(message);
    setToastType(type);
    setShowToast(true);
    setTimeout(() => setShowToast(false), 3000);
  }, []);

  const generateJSON = useCallback(() => {
    const processSections = (sections) => {
      const result = {};
      Object.entries(sections).forEach(([sectionName, section]) => {
        result[sectionName] = {
          type: section.type,
          config: section.config,
          llmActions: (section.llmActions || []).reduce((acc, action) => {
            if (action.name && action.enabled) {
              acc[action.name] = {
                prompt: action.prompt,
                feedbackType: action.feedbackType,
                formatAsJson: action.formatAsJson
              };
            }
            return acc;
          }, {})
        };
        
        if (section.nestedSections && Object.keys(section.nestedSections).length > 0) {
          result[sectionName].nestedSections = processSections(section.nestedSections);
        }
      });
      return result;
    };

    const output = {
      includeData: { ...config.includeData, question_scores: true },
      sections: processSections(config.sections)
    };
    
    return JSON.stringify(output, null, 2);
  }, [config]);

  // Load configuration from JSON (string or object)
  const loadFromJSON = useCallback((input) => {
    try {
      const parsed = typeof input === 'string' ? JSON.parse(input) : input;
      if (!parsed || typeof parsed !== 'object') {
        throw new Error('Invalid JSON configuration');
      }

      const toArrayOfActions = (actionsObj = {}) => {
        return Object.entries(actionsObj).map(([name, a]) => ({
          id: Date.now() + Math.floor(Math.random() * 1000000),
          name,
          prompt: a?.prompt || '',
          feedbackType: a?.feedbackType || 'current',
          enabled: a?.enabled !== false,
          formatAsJson: !!a?.formatAsJson
        }));
      };

      const processSections = (sectionsObj = {}) => {
        const result = {};
        Object.entries(sectionsObj).forEach(([sectionName, sectionVal]) => {
          const type = sectionVal?.type || 'anchor';
          const baseSection = {
            id: Date.now() + Math.floor(Math.random() * 1000000),
            name: sectionName,
            type,
            config: sectionVal?.config || {},
            llmActions: toArrayOfActions(sectionVal?.llmActions),
            nestedSections: type === 'anchor' ? {} : undefined
          };

          if (sectionVal?.nestedSections && typeof sectionVal.nestedSections === 'object') {
            baseSection.nestedSections = processSections(sectionVal.nestedSections);
          }

          result[sectionName] = baseSection;
        });
        return result;
      };

      const nextConfig = {
        includeData: {
          name: parsed?.includeData?.name !== false,
          score: parsed?.includeData?.score !== false,
          change: parsed?.includeData?.change !== false,
          benchmark: parsed?.includeData?.benchmark !== false,
          outcomeScores: parsed?.includeData?.outcomeScores !== false,
          llmActions: parsed?.includeData?.llmActions !== false,
          question_scores: true,
          score_distribution: parsed?.includeData?.score_distribution === true,
          score_distribution_bands: parsed?.includeData?.score_distribution_bands || { low: [0, 4], neutral: [5, 6], high: [7, 10] },
          anchor_breakdown: parsed?.includeData?.anchor_breakdown === true,
          include_report_summary: parsed?.includeData?.include_report_summary === true,
          include_action_recommendations: parsed?.includeData?.include_action_recommendations === true
        },
        sections: processSections(parsed?.sections || {})
      };

      setConfig(nextConfig);
      setShowToast(true);
      setToastType('success');
      setToastMessage('JSON configuration loaded.');
      setTimeout(() => setShowToast(false), 3000);
    } catch (err) {
      console.warn('Failed to load JSON configuration:', err);
      setShowToast(true);
      setToastType('error');
      setToastMessage('Failed to load JSON: ' + (err?.message || 'Unknown error'));
      setTimeout(() => setShowToast(false), 3000);
    }
  }, []);

  // Validation functions
  const validateSectionName = useCallback((name, parentName = null) => {
    try {
      let sections = {};
      
      if (parentName) {
        const parentSection = config.sections[parentName];
        if (parentSection && parentSection.nestedSections) {
          sections = parentSection.nestedSections;
        }
      } else {
        sections = config.sections;
      }
      
      const existingNames = Object.keys(sections).filter(key => key !== name);
      if (existingNames.some(key => key.toLowerCase() === name.toLowerCase())) {
        return `A section with the name "${name}" already exists.`;
      }
      return '';
    } catch (error) {
      console.warn('Error validating section name:', error);
      return '';
    }
  }, [config.sections]);

  const validateNestedSectionName = useCallback((name, parentName) => {
    try {
      const parentSection = config.sections[parentName];
      if (!parentSection || !parentSection.nestedSections) {
        return '';
      }
      
      const nestedSections = parentSection.nestedSections;
      const existingNames = Object.keys(nestedSections).filter(key => key !== name);
      if (existingNames.some(key => key.toLowerCase() === name.toLowerCase())) {
        return `A nested section with the name "${name}" already exists.`;
      }
      return '';
    } catch (error) {
      console.warn('Error validating nested section name:', error);
      return '';
    }
  }, [config.sections]);

  const validateLLMActionName = useCallback((name, parentName) => {
    try {
      let llmActions = [];
      
      if (parentName) {
        // For nested sections
        const parentSection = config.sections[parentName];
        if (parentSection && parentSection.nestedSections) {
          const nestedSection = parentSection.nestedSections[name];
          if (nestedSection && nestedSection.llmActions) {
            llmActions = nestedSection.llmActions;
          }
        }
      } else {
        // For top-level sections
        const section = config.sections[name];
        if (section && section.llmActions) {
          llmActions = section.llmActions;
        }
      }
      
      const existingNames = llmActions
        .filter(action => action.name !== name)
        .map(action => action.name);
        
      if (existingNames.some(key => key.toLowerCase() === name.toLowerCase())) {
        return `An LLM action with the name "${name}" already exists.`;
      }
      return '';
    } catch (error) {
      console.warn('Error validating LLM action name:', error);
      return '';
    }
  }, [config.sections]);

  return {
    // State
    config,
    showAddSection,
    showAddNestedSection,
    showAddLLMActionModal,
    showEditLLMActionModal,
    showEditSectionConfigModal,
    showRemoveSectionModal,
    showRemoveLLMActionModal,
    newSectionType,
    newSectionName,
    newNestedSectionType,
    newNestedSectionName,
    parentSectionName,
    selectedSectionName,
    selectedActionId,
    actionName,
    actionPrompt,
    actionFeedbackType,
    actionEnabled,
    actionFormatAsJson,
    llmActionsToEdit,
    selectedSectionConfigField,
    selectedSectionConfigValue,
    sectionToRemove,
    llmActionsToRemove,
    openSections,
    showToast,
    toastMessage,
    toastType,
    isLoading,

    // Setters
    setShowAddSection,
    setShowAddNestedSection,
    setShowAddLLMActionModal,
    setShowEditLLMActionModal,
    setShowEditSectionConfigModal,
    setShowRemoveSectionModal,
    setShowRemoveLLMActionModal,
    setNewSectionType,
    setNewSectionName,
    setNewNestedSectionType,
    setNewNestedSectionName,
    setParentSectionName,
    setSelectedSectionName,
    setSelectedActionId,
    setActionName,
    setActionPrompt,
    setActionFeedbackType,
    setActionEnabled,
    setActionFormatAsJson,
    setLLMActionsToEdit,
    setSelectedSectionConfigField,
    setSelectedSectionConfigValue,
    setSectionToRemove,
    setLLMActionsToRemove,
    setOpenSections,
    setIncludeDataField,
    setShowToast,
    setToastMessage,
    setToastType,
    setIsLoading,

    // Functions
    addSection,
    removeSection,
    updateSectionConfig,
    addLLMAction,
    removeLLMAction,
    updateLLMAction,
    toggleIncludeData,
    toggleSection,
    isSectionOpen,
    showToastMessage,
    generateJSON,
    loadFromJSON,
    validateSectionName,
    validateNestedSectionName,
    validateLLMActionName
  };
}; 